<template>
    <v-card outlined>
        <v-toolbar flat pb-5 mb-5>
            <v-toolbar-title class="text-uppercase body-1 font-weight-medium">Interval Data</v-toolbar-title>
            <v-divider class="mx-4" inset vertical></v-divider>

            <div class="flex-grow-1"></div>

            <!-- search ~*~*~*~*~*~*~*~*~*~*~*~*~*~* -->
            <v-col cols="3" sm="3" md="3">
                <v-text-field
                    v-model="search"
                    append-icon="search"
                    label="Search"
                    single-line
                    hide-details
                    ></v-text-field>
            </v-col>
        </v-toolbar>

        <!-- table ~*~*~*~*~*~*~*~*~*~*~*~*~*~* -->
        <v-data-table
            :headers="headers"
            :items="intervals"
            :single-expand="false"
            :expanded.sync="expanded"
            :search="search"
            :sort-by="['ending_local']"
            :sort-desc="[false]"
            :items-per-page="15"
            item-key="ending_local"
            show-expand
            multi-sort
            >
            <!-- header ~*~*~*~*~*~*~*~*~*~*~*~*~*~* -->
            <template v-slot:top>
                <p>&nbsp;</p>
            </template>
            <!-- no-data ~*~*~*~*~*~*~*~*~*~*~*~*~*~* -->
            <template slot="no-data" class="text-left">
                <span class="subheading text-left">
                    <v-icon color="orange">warning</v-icon>
                    No data found. Please select a different date and time.
                </span>
            </template>
            <!-- expandsion button ~*~*~*~*~*~*~*~* -->
            <!-- display/not display expansion icon & panel -->
            <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
                <v-btn @click="expand(true)" v-if="item.programs.length > 0 && !isExpanded" text icon color="orange">
                    <v-icon>arrow_drop_down</v-icon>
                </v-btn>
                <v-btn @click="expand(false)" v-if="item.programs.length > 0 && isExpanded" text icon color="green">
                    <v-icon>arrow_drop_up</v-icon>
                </v-btn>
            </template>
            <!-- content ~*~*~*~*~*~*~*~*~*~*~*~*~*~*-->
            <template v-slot:item.ending_local="{ item }">
                {{ item.ending_local | $_datetimeFilter_timestamp_no_seconds}}
            </template>
            <template v-slot:item.guidance="{ item }">
                {{ item.guidance | $_dataFilter_guidance }}
            </template>
            <template v-slot:item.load_kw="{ item }">
                {{ item.load_kw | $_powerFilter_kw }}
            </template>
            <template v-slot:item.optimal_power_output_kw="{ item }">
                {{ item.optimal_power_output_kw | $_powerFilter_kw }}
            </template>
            <template v-slot:item.actual_power_output_kw="{ item }">
                {{ item.actual_power_output_kw | $_powerFilter_kw }}
            </template>
            <template v-slot:item.unrealized_power_output_kw="{ item }">
                {{ item.unrealized_power_output_kw | $_powerFilter_kw }}
            </template>
            <template v-slot:item.value_at_stake="{ item }">
                {{ item.value_at_stake | $_currencyFilter_usd }}
            </template>
            <template v-slot:item.actual_value_at_stake="{ item }">
                {{ item.actual_value_at_stake | $_currencyFilter_usd }}
            </template>
            <template v-slot:item.achieved_power_output_percent="{ item }">
                {{ item.achieved_power_output_percent | $_percentFilter_calcPercentCap }}
            </template>
            <!-- expansion panel data  ~*~*~*~*~*~*~*~* -->
            <template v-slot:expanded-item="{ item }">
                <td><!-- placeholder for expansion icon column, better presentation --></td>
                <td :colspan="headers.length">
                    <!-- <pre>{{ item.programs }}</pre> -->
                    <v-simple-table class="sustaine-expanded-table">
                        <template v-slot:default>
                            <thead>
                                <tr>
                                    <th v-for="(header, h) in subheaders" 
                                        :key="h" 
                                        class="font-weight-regular text-left"
                                        >
                                        {{ header.text }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(program, p) in item.programs" :key="p">
                                    <td>{{ program.description }}</td>
                                    <td>{{ program.guidance | $_dataFilter_guidance }}</td>
                                    <td>{{ program.program_kw | $_powerFilter_kw }}</td>
                                    <td>{{ program.program_price_mw | $_currencyFilter_usd }}</td>
                                    <td>{{ program.value_at_stake | $_currencyFilter_usd }}</td>
                                    <td>{{ actualValue(program)  | $_currencyFilter_usd }}</td>
                                    <td>
                                        <h3 class="headline font-weight-bold compress">
                                            {{ program.performance | $_percentFilter_calcPercentCap }}
                                        </h3>
                                    </td>
                                </tr>
                            </tbody>
                        </template>
                    </v-simple-table>
                </td>
            </template>
        </v-data-table>
    </v-card>
</template>
<script>
export default {
    name: "PerformanceTable",
    props: {
        intervals: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            search: '',
            expanded: [],
            singleExpand: false,
            headers: [
                {
                    text: 'ENDING',
                    align: 'right',
                    sortable: true,
                    value: 'ending_local'
                },
                {
                    text: 'GUIDANCE',
                    align: 'right',
                    sortable: true,
                    value: 'guidance'
                },
                {
                    text: 'LOAD',
                    align: 'right',
                    sortable: true,
                    value: 'load_kw'
                },
                {
                    text: 'TARGET OUTPUT',
                    align: 'right',
                    sortable: true,
                    value: 'optimal_power_output_kw'
                },
                {
                    text: 'ACTUAL OUTPUT',
                    align: 'right',
                    sortable: true,
                    value: 'actual_power_output_kw'
                },
                {
                    text: 'UNREALIZED',
                    align: 'right',
                    sortable: true,
                    value: 'unrealized_power_output_kw'
                },
                {
                    text: 'TARGET VALUE',
                    align: 'right',
                    sortable: true,
                    value: 'value_at_stake'
                },
                {
                    text: 'ACTUAL VALUE',
                    align: 'right',
                    sortable: true,
                    value: 'actual_value_at_stake'
                },
                {
                    text: 'PERFORMANCE',
                    align: 'right',
                    sortable: true,
                    value: 'achieved_power_output_percent'
                }
            ],
            subheaders: [
                {
                    text: 'PROGRAM',
                    align: 'right',
                    sortable: true,
                    value: 'description'
                },
                {
                    text: 'GUIDANCE',
                    align: 'right',
                    sortable: true,
                    value: 'guidance'
                },
                {
                    text: 'PROGRAM (KW)',
                    align: 'right',
                    sortable: true,
                    value: 'program_kw'
                },
                {
                    text: 'PROGRAM PRICE ($/kW)',
                    align: 'right',
                    sortable: true,
                    value: 'program_price_mw'
                },
                {
                    text: 'TARGET VALUE',
                    align: 'right',
                    sortable: true,
                    value: 'value_at_stake'
                },
                {
                    text: 'ACTUAL VALUE',
                    align: 'right',
                    sortable: true,
                    value: 'value_at_stake'
                },
                {
                    text: 'PERFORMANCE',
                    align: 'right',
                    sortable: true,
                    value: 'performance'
                }
            ]
        }
    },
    methods: {
        actualValue(program) {
            return program.value_at_stake * parseFloat(program.performance).toFixed(2)
        }
    }
}
</script>
<style lang="scss" scoped>
.sustaine-expanded-table {
  background-color:rgba(0, 0, 0, 0) !important; /* transparent */
  th {
    color: #335b98 !important;
    font-weight: 600 !important;
  }
}
</style>
